/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ChannelsInteractor } from "../../model/interactor";
import type { ActionKernelType } from "../../model/types";
import {
  MetadataState,
  PlaybackContentType,
  PlayerState,
  SessionState
} from "../../model/videoplayer/enums";
import type { IPlayable } from "../../model/videoplayer/interfaces";
import type { TSeekRange } from "../../model/videoplayer/types";
import type { TrickPlayMode } from "../../model/videoplayer/trickplay";
import type { Direction, SpeedLevel } from "../../model/videoplayer/trickplay";
import type { ITrickPlayEligibility } from "../../model/videoplayer/interfaces";
import type { SeekRangeMode } from "../../model/videoplayer/interfaces";
import type { UnityRootState } from "../UnityAppStore";
import type {
  AudioTrackUiOption,
  TOnePassActionInfo
} from "./videoPlayerUi.types";

const START_OVER_ACTION_KERNEL: ActionKernelType =
  "watchFromSocuActionKernel" as ActionKernelType;

const RECORD_ACTION_KERNELS: ReadonlySet<ActionKernelType> =
  new Set<ActionKernelType>([
    "recordAgainActionKernel" as ActionKernelType,
    "recordThisShowActionKernel" as ActionKernelType,
    "recordThisMovieActionKernel" as ActionKernelType,
    "recordThisEpisodeActionKernel" as ActionKernelType,
    "recordThisGameActionKernel" as ActionKernelType,
    "recordThisMatchActionKernel" as ActionKernelType,
    "recordThisRaceActionKernel" as ActionKernelType,
    "recordThisEventActionKernel" as ActionKernelType,
    "modifyRecordingActionKernel" as ActionKernelType,
    "recoverRecordingActionKernel" as ActionKernelType,
    "stopAndDeleteRecordingActionKernel" as ActionKernelType,
    "deleteRecordingActionKernel" as ActionKernelType,
    "cancelRecordingActionKernel" as ActionKernelType
  ]);

/**
 * Present once the airing already has a scheduled recording: the server swaps
 * Record out for Modify recording, so the two are mutually exclusive in a
 * given action list.
 */
const MODIFY_RECORDING_ACTION_KERNEL: ActionKernelType =
  "modifyRecordingActionKernel" as ActionKernelType;

const ONE_PASS_CREATE_ACTION_KERNELS: ReadonlySet<ActionKernelType> =
  new Set<ActionKernelType>([
    "createOnePassActionKernelV2" as ActionKernelType,
    "createMultiOnePassActionKernel" as ActionKernelType
  ]);

/** Present once the series already has a OnePass. */
const ONE_PASS_MODIFY_ACTION_KERNELS: ReadonlySet<ActionKernelType> =
  new Set<ActionKernelType>([
    "modifyOnePassActionKernel" as ActionKernelType,
    "modifyOnePassActionKernelV2" as ActionKernelType,
    "modifyMultiOnePassActionKernel" as ActionKernelType
  ]);

// ── Playback selectors ──

/** Returns the current playback time in seconds. */
export const selectCurrentTime = (state: UnityRootState): number =>
  state.videoPlayerUi.currentTime;

/** Returns the total duration in seconds. */
export const selectDuration = (state: UnityRootState): number =>
  state.videoPlayerUi.duration;

/** Returns the current player state enum value. */
export const selectPlayerState = (state: UnityRootState): PlayerState =>
  state.videoPlayerUi.playerState;

/** Returns true when the player is in PLAYING state. */
export const selectIsPlaying = (state: UnityRootState): boolean =>
  state.videoPlayerUi.playerState === PlayerState.PLAYING;

/** Returns true while the player is in a transient BUFFERING stall (not a pause). */
export const selectIsBufferingStall = (state: UnityRootState): boolean =>
  state.videoPlayerUi.playerState === PlayerState.BUFFERING;

/** Returns the seek range from the player. */
export const selectSeekRange = (state: UnityRootState): TSeekRange =>
  state.videoPlayerUi.seekRange;

// ── Airing info selectors ──

/** Returns the formatted airing start time label (e.g. "9:30 PM"). */
export const selectStartTimeLabel = (state: UnityRootState): string =>
  state.videoPlayerUi.startTimeLabel;

/** Returns the formatted airing end time label (e.g. "10:30 PM"). */
export const selectEndTimeLabel = (state: UnityRootState): string =>
  state.videoPlayerUi.endTimeLabel;

/** Returns the airing start time as epoch seconds. */
export const selectAiringStartEpoch = (state: UnityRootState): number =>
  state.videoPlayerUi.airingStartEpoch;

/** Returns the airing end time as epoch seconds. */
export const selectAiringEndEpoch = (state: UnityRootState): number =>
  state.videoPlayerUi.airingEndEpoch;

/** Returns the parental control rating. */
export const selectParentalControlsRating = (
  state: UnityRootState
): ReadonlyArray<Record<string, unknown>> =>
  state.videoPlayerUi.parentalControlsRating;

/** Returns the program title of the current content . */
export const selectProgramDetails = (state: UnityRootState): Array<string> =>
  state.videoPlayerUi.programDetails;

/** Returns the latest metadata state emitted by VideoPlaybackController. */
export const selectMetadataState = (state: UnityRootState): MetadataState =>
  state.videoPlayerUi.metadataState;

/** Returns the latest session state emitted by VideoPlaybackController. */
export const selectSessionState = (state: UnityRootState): SessionState =>
  state.videoPlayerUi.sessionState;

// ── Trick play selectors ──

/** Returns whether trick play is currently active. */
export const selectIsTrickPlayActive = (state: UnityRootState): boolean =>
  state.videoPlayerUi.isTrickPlayActive;

/** Returns the current trick play mode, or null if inactive. */
export const selectTrickPlayMode = (
  state: UnityRootState
): TrickPlayMode | null => state.videoPlayerUi.trickPlayMode;

/** Returns the target seek position in seconds during trick play. */
export const selectTargetPosition = (state: UnityRootState): number =>
  state.videoPlayerUi.targetPosition;

// ── Derived selectors ──

/**
 * Returns true when the player is in a buffering-like state (BUFFERING,
 * LOADING, READY, SEEKING, ERROR_RECOVERY).
 *
 * ERROR_RECOVERY belongs here because an automatic retry is a wait like any
 * other from the viewer's side: the picture has stopped and the player is
 * working on it. Without it the replay runs behind a frozen frame with no
 * indication anything is happening.
 */
export const selectIsBuffering = (state: UnityRootState): boolean => {
  const ps: PlayerState = state.videoPlayerUi.playerState;
  return (
    ps === PlayerState.BUFFERING ||
    ps === PlayerState.LOADING ||
    ps === PlayerState.READY ||
    ps === PlayerState.SEEKING ||
    ps === PlayerState.ERROR_RECOVERY
  );
};

/**
 * Returns true while the player is replaying to recover from a failure.
 *
 * Separate from {@link selectIsBuffering} because the loading indicator skips
 * its grace delay here: the picture already stopped when the failure hit, so
 * there is no short-stall case to wait out.
 */
export const selectIsErrorRecovery = (state: UnityRootState): boolean =>
  state.videoPlayerUi.playerState === PlayerState.ERROR_RECOVERY;

/** Returns the trick play target position when active, or null when inactive. */
export const selectSeekPosition = (state: UnityRootState): number | null =>
  state.videoPlayerUi.isTrickPlayActive
    ? state.videoPlayerUi.targetPosition
    : null;

// ── Playable selectors ──

/** Returns the currently playing playable, or null if none. */
export const selectPlayable = (state: UnityRootState): IPlayable | null =>
  state.videoPlayerUi.playable;

/**
 * Returns true when the channel currently playing is a TiVo Plus channel.
 *
 * The player's counterpart to the guide's focused-channel selector: the
 * transport controls need the rule for whatever is on screen, which is not
 * necessarily what the guide has focused.
 */
export const selectIsPlayingTivoPlusChannel = (
  state: UnityRootState
): boolean => {
  const playable: IPlayable | null = state.videoPlayerUi.playable;

  if (playable === null || playable.channel === undefined) {
    return false;
  }

  return ChannelsInteractor.isTivoPlusChannel(playable.channel);
};

/** Returns the current seek direction during a D-pad skip, or null when idle. */
export const selectSeekDirection = (state: UnityRootState): Direction | null =>
  state.videoPlayerUi.seekDirection;

/** Returns the monotonic skip counter. */
export const selectSkipCounter = (state: UnityRootState): number =>
  state.videoPlayerUi.skipCounter;

// ── Trick-play eligibility / speed selectors ──

/** Returns the current trick-play eligibility (PRD §5). */
export const selectTrickPlayEligibility = (
  state: UnityRootState
): ITrickPlayEligibility => state.videoPlayerUi.trickPlayEligibility;

/** Whether the restriction API currently allows trick-play (TP-AP-1). */
export const selectTrickPlayRestrictionEnabled = (
  state: UnityRootState
): boolean => state.videoPlayerUi.trickPlayRestrictionEnabled;

/** Whether the seek bar should be interactive (eligibility AND restriction). */
export const selectIsTrickPlayInteractive = (state: UnityRootState): boolean =>
  state.videoPlayerUi.trickPlayEligibility.trickPlayAllowed &&
  state.videoPlayerUi.trickPlayRestrictionEnabled;

/** Returns the current seek range mode (full, forward-only, backward-only, none). */
export const selectSeekRangeMode = (state: UnityRootState): SeekRangeMode =>
  state.videoPlayerUi.trickPlayEligibility.seekRangeMode;

/** Whether a dedicated Start Over (SOCU) stream is available.
 *  Used by SeekBar to determine fill reference (program start vs buffer start). */
export const selectIsStartOverAvailable = (state: UnityRootState): boolean =>
  state.videoPlayerUi.trickPlayEligibility.startOverAvailable;

/** Whether forward seeking/scrubbing is allowed by restrictions. */
export const selectAllowForward = (state: UnityRootState): boolean =>
  state.videoPlayerUi.trickPlayEligibility.allowForward;

/** Whether backward seeking/scrubbing is allowed by restrictions. */
export const selectAllowBackward = (state: UnityRootState): boolean =>
  state.videoPlayerUi.trickPlayEligibility.allowBackward;

/** Current speed level during an active hold / cycle session. */
export const selectCurrentSpeedLevel = (
  state: UnityRootState
): SpeedLevel | null => state.videoPlayerUi.currentSpeedLevel;

/** Whether the current speed session was triggered by a long press. */
export const selectSpeedTriggeredByLongPress = (
  state: UnityRootState
): boolean => state.videoPlayerUi.speedTriggeredByLongPress;

/** Seek entry point in seconds (player position at session start). */
export const selectSeekEntryPoint = (state: UnityRootState): number | null =>
  state.videoPlayerUi.seekEntryPoint;

/** Whether the player is currently playing a Start Over (SOCU) stream. */
export const selectIsStartOverPlaying = (state: UnityRootState): boolean =>
  state.videoPlayerUi.isStartOverPlaying;

/** Content types that represent Catch Up (past-program) playback. */
const CATCH_UP_CONTENT_TYPES: ReadonlySet<PlaybackContentType> =
  new Set<PlaybackContentType>([
    PlaybackContentType.CATCHUP_BEGINNING,
    PlaybackContentType.CATCHUP_RESUME
  ]);

/** Whether the player is currently playing a Catch Up (SOCU) stream. */
export const selectIsCatchUpPlaying = (state: UnityRootState): boolean => {
  const playable: IPlayable | null = state.videoPlayerUi.playable;
  if (playable === null) {
    return false;
  }
  return CATCH_UP_CONTENT_TYPES.has(playable.playbackContentType);
};

/** Content types that represent nDVR (recording) playback. */
const NDVR_CONTENT_TYPES: ReadonlySet<PlaybackContentType> =
  new Set<PlaybackContentType>([
    PlaybackContentType.NDVR_LIVE,
    PlaybackContentType.NDVR_RECORDED
  ]);

/** Whether the player is currently playing an nDVR recording. */
export const selectIsNdvrPlaying = (state: UnityRootState): boolean => {
  const playable: IPlayable | null = state.videoPlayerUi.playable;
  if (playable === null) {
    return false;
  }
  return NDVR_CONTENT_TYPES.has(playable.playbackContentType);
};

/** Whether the player is currently playing VOD (on-demand) content. */
export const selectIsVodPlaying = (state: UnityRootState): boolean => {
  const playable: IPlayable | null = state.videoPlayerUi.playable;
  if (playable === null) {
    return false;
  }
  return playable.playbackContentType === PlaybackContentType.VOD;
};

/**
 * Whether the player is on a SOCU stream — Start Over or Catch Up. Both are
 * VOD-kind playback whose player clock is absolute epoch seconds, so they share
 * the elapsed-time seek-bar presentation (no wall-clock start label, position
 * shown as elapsed time within the program).
 */
export const selectIsSocuPlaying = (state: UnityRootState): boolean =>
  state.videoPlayerUi.isStartOverPlaying || selectIsCatchUpPlaying(state);

/**
 * Whether the playback position has settled (first frame produced). Withholds
 * the seek-bar knob, current-time label, and thumbnail until true so they do
 * not flash at a transient position. Applies to all playback types.
 */
export const selectIsPositionSettled = (state: UnityRootState): boolean =>
  state.videoPlayerUi.isPositionSettled;

// ── Audio track selectors ──

/** Returns the available audio tracks as display options. */
export const selectAudioTracks = (
  state: UnityRootState
): ReadonlyArray<AudioTrackUiOption> => state.videoPlayerUi.audioTracks;

/** Returns the id of the currently active audio track, or null when none. */
export const selectSelectedAudioTrackId = (
  state: UnityRootState
): number | null => state.videoPlayerUi.selectedAudioTrackId;

/** Returns whether audio/text track metadata has finished loading for the current tune. */
export const selectAreMediaTracksReady = (state: UnityRootState): boolean =>
  state.videoPlayerUi.areMediaTracksReady;

/** Returns whether PiG was entered via a mini-player click from the Guide. */
export const selectWasPiGEnteredFromMiniClick = (
  state: UnityRootState
): boolean => state.videoPlayerUi.wasPiGEnteredFromMiniClick;

/** Returns action kernel types from the latest metadata actions list. */
export const selectActionKernelTypes = (
  state: UnityRootState
): ReadonlyArray<ActionKernelType> => state.videoPlayerUi.actionKernelTypes;

/** Returns true when metadata actions indicate Start Over is applicable. */
export const selectHasStartOverActionKernel = (
  state: UnityRootState
): boolean => {
  return state.videoPlayerUi.actionKernelTypes.includes(
    START_OVER_ACTION_KERNEL
  );
};

/** Returns true when metadata actions indicate Record is applicable. */
export const selectHasRecordActionKernel = (state: UnityRootState): boolean => {
  return state.videoPlayerUi.actionKernelTypes.some(
    (kernelType: ActionKernelType): boolean =>
      RECORD_ACTION_KERNELS.has(kernelType)
  );
};

/**
 * Returns true when metadata actions offer Modify recording, i.e. this airing
 * is already scheduled. Callers should hide Record when this is true —
 * `selectHasRecordActionKernel` treats the whole recording family as one, so
 * it stays true here too.
 */
export const selectHasModifyRecordingActionKernel = (
  state: UnityRootState
): boolean => {
  return state.videoPlayerUi.actionKernelTypes.includes(
    MODIFY_RECORDING_ACTION_KERNEL
  );
};

/**
 * Returns the collection id to open Modify recording against, or null when the
 * action list carries none — the caller then has nothing specific to manage.
 */
export const selectModifyRecordingCollectionId = (
  state: UnityRootState
): string | null => state.videoPlayerUi.modifyRecordingCollectionId;

/** Returns true when metadata actions contain a OnePass create action. */
export const selectHasOnePassCreateActionKernel = (
  state: UnityRootState
): boolean => {
  return state.videoPlayerUi.actionKernelTypes.some(
    (kernelType: ActionKernelType): boolean =>
      ONE_PASS_CREATE_ACTION_KERNELS.has(kernelType)
  );
};

/**
 * Returns the ucid and title from the create-OnePass action in the latest
 * metadata, or null if no such action is present.
 */
export const selectOnePassActionInfo = (
  state: UnityRootState
): TOnePassActionInfo | null => state.videoPlayerUi.onePassActionInfo;

/** Returns true when metadata actions contain a OnePass modify action. */
export const selectHasOnePassModifyActionKernel = (
  state: UnityRootState
): boolean => {
  return state.videoPlayerUi.actionKernelTypes.some(
    (kernelType: ActionKernelType): boolean =>
      ONE_PASS_MODIFY_ACTION_KERNELS.has(kernelType)
  );
};

/**
 * Returns the ucid and title from the modify-OnePass action in the latest
 * metadata, or null if no such action carries a collection id.
 */
export const selectOnePassModifyInfo = (
  state: UnityRootState
): TOnePassActionInfo | null => state.videoPlayerUi.onePassModifyInfo;

/**
 * Whether the model has requested a return to the previous screen after a long
 * background (Catchup / nDVR / VOD). The View acts on this and clears it.
 */
export const selectShouldExitToPreviousScreen = (
  state: UnityRootState
): boolean => state.videoPlayerUi.shouldExitToPreviousScreen;
